export interface IAgeRatings {
    PEGI: PEGI;
    USK: USK;
    ESRB: ESRB;
    DJCTQ: DJCTQ;
    ACB: ACB;
    OFLC: OFLC;
    RARS: RARS;
    CERO: CERO;
    GRAC: GRAC;
    GSRR: GSRR;
    GCAM: GCAM;
}

export const AGE_RATING_SYSTEMS = {
    PEGI: 'PEGI',
    USK: 'USK',
    ESRB: 'ESRB',
    DJCTQ: 'DJCTQ (ClassInd)',
    ACB: 'ACB',
    OFLC: 'OFLC',
    RARS: 'RARS',
    CERO: 'CERO',
    GRAC: 'GRAC',
    GSRR: 'GSRR',
    GCAM: 'GCAM',
};

export const AGE_RATING_AREAS = {
    PEGI: '(EMEA)',
    USK: '(Germany)',
    ESRB: '(NCSA)',
    DJCTQ: '(Brazil)',
    ACB: '(Australia)',
    OFLC: '(New Zeland)',
    RARS: '(Russia)',
    CERO: '(Japan)',
    GRAC: '(South Korea)',
    GSRR: '(Taiwan)',
    GCAM: '(MENA)'
};

export enum PEGI {
    PEGI_3 = 'PEGI 3',
    PEGI_7 = 'PEGI 7',
    PEGI_12 = 'PEGI 12',
    PEGI_16 = 'PEGI 16',
    PEGI_18 = 'PEGI 18'
}

export enum USK {
    USK_RP = 'RP',
    USK_0 = 'USK 0',
    USK_6 = 'USK 6',
    USK_12 = 'USK 12',
    USK_16 = 'USK 16',
    USK_18 = 'USK 18'
}

export enum ESRB {
    ESRB_RP = 'RP',
    ESRB_EC = 'EC',
    ESRB_E = 'E',
    ESRB_E10plus = 'E10+',
    ESRB_T = 'T',
    ESRB_M = 'M',
    ESRB_AO = 'AO'
}

export enum DJCTQ {
    DJCTQ_L = 'L',
    DJCTQ_10 = '10',
    DJCTQ_12 = '12',
    DJCTQ_14 = '14',
    DJCTQ_16 = '16',
    DJCTQ_18 = '18'
}

export enum ACB {
    ACB_CTC = 'CTC',
    ACB_E = 'E',
    ACB_G = 'G',
    ACB_PG = 'PG',
    ACB_M = 'M',
    ACB_MA15plus = 'MA 15+',
    ACB_R18plus = 'R 18+',
    ACB_X18plus = 'X 18+'
}

export enum OFLC {
    OFLC_G = 'G',
    OFLC_PG = 'PG',
    OFLC_M = 'M',
    OFLC_R13 = 'R13',
    OFLC_R15 = 'R15',
    OFLC_R16 = 'R16',
    OFLC_R18 = 'R18',
    OFLC_R = 'R'
}

export enum RARS {
    RARS_0plus = '0+',
    RARS_6plus = '6+',
    RARS_12plus = '12+',
    RARS_16plus = '16+',
    RARS_18plus = '18+'
}

export enum CERO {
    CERO_RP = 'RP',
    CERO_A = 'A',
    CERO_B12plus = 'B (12+)',
    CERO_C15plus = 'C (15+)',
    CERO_D17plus = 'D (17+)',
    CERO_Z18plus = 'Z (18+)'
}

export enum GRAC {
    GRAC_RP = 'RP',
    GRAC_All = 'All',
    GRAC_12 = '12',
    GRAC_15 = '15',
    GRAC_18 = '18'
}

export enum GSRR {
    GSRR_G = 'G',
    GSRR_P = 'P',
    GSRR_PG12 = 'PG 12',
    GSRR_PG15 = 'PG 15',
    GSRR_R = 'R'
}

export enum GCAM {
    GCAM_3 = '3',
    GCAM_7 = '7',
    GCAM_12 = '12',
    GCAM_16 = '16',
    GCAM_18 = '18',
    GCAM_TBC = 'To be classified'
}

export const AGE_RATING_SYSTEMS_TO_VALUES_MAP = {
    PEGI: PEGI,
    USK: USK,
    ESRB: ESRB,
    DJCTQ: DJCTQ,
    ACB: ACB,
    OFLC: OFLC,
    RARS: RARS,
    CERO: CERO,
    GRAC: GRAC,
    GSRR: GSRR,
    GCAM: GCAM,
};

/**
 * Map of age ratings systems to object with age ratings keys({ageRatingKey: "ageRatingKey"}).
 *  Example:
 * {
 *     USK: {
 *         USK_18: "USK_18",
 *         ...
 *     }
 * }
 */
export const AGE_RATINGS_SYSTEMS_TO_VALUES_KEYS_MAP: { [ageRatingSystem: string]: { [ageRatingSubSystem: string]: string } } = Object.keys(
    AGE_RATING_SYSTEMS
).reduce((result, ratingKey) => {
    result[ratingKey] = Object.keys(AGE_RATING_SYSTEMS_TO_VALUES_MAP[ratingKey]).reduce((_result, _ratingKey) => {
        _result[_ratingKey] = _ratingKey;
        return _result;
    }, {});
    return result;
}, {});

/**
 * Map of age ratings systems to array of arrays with age ratings keys and values.
 * Example:
 * {
 *      PEGI: [
 *          ["PEGI_3", "PEGI 3"],
 *          ...
 *      ]
 * }
 */
export const RATINGS_MAP: { [ageRatingSystem: string]: Array<[string, string]> } = Object.keys(AGE_RATING_SYSTEMS).reduce(
    (result, ratingKey) => {
        result[ratingKey] = Object.entries(AGE_RATING_SYSTEMS_TO_VALUES_MAP[ratingKey]);
        return result;
    },
    {}
);

/**
 *  Example:
 * {
 *     USK: "",
 *     ACB: "",
 *     ...
 * }
 */
export const DEFAULT_AGE_RATINGS: { [ageRatingSystem: string]: string } = Object.keys(AGE_RATING_SYSTEMS).reduce((memo, system) => {
    memo[system] = '';
    return memo;
}, {});

export const RATING_EMPTY = 'N/A';

export type Rating = ACB | CERO | DJCTQ | ESRB | GRAC | GSRR | PEGI | RARS | USK | OFLC | GCAM | string;
